import { useEffect, createContext, type PropsWithChildren, useCallback, useState } from 'react';

import axios from 'axios';
import { useContainer } from 'unstated-next';

import AdminAuthContainer from './AdminAuthContainer';
import { GET_USER_DATA } from '../../config/apiPaths';
import { getUrls, processApiPath } from '../../utils/url';

export interface PortalProviderProps {
    settingsId: string | null;
    settingsUuid: string;
    onBack?: () => void;
    narrowStyle?: boolean;
    fullScreen?: boolean;
}

interface PortalContextInterface {
    settingsId: string | null;
    loading: boolean;
    isNarrowStyle: boolean;
    isFullScreen: boolean;
    errorMessage: string;
    errorModalMessage: string;
    setErrorModalMessage: (message: string) => void;
    openErrorModal: boolean;
    setOpenErrorModal: (openEditModal: boolean) => void;
    settingsData: any;
    setSettingsData: (settingsData: any) => void;
    onBack?: () => void;
    me: any;
    meFetched: boolean;
}

const PortalContext = createContext(undefined as unknown as PortalContextInterface);

const { Provider, Consumer } = PortalContext;

const PortalProvider = (props: PropsWithChildren<PortalProviderProps>) => {
    const search = new URLSearchParams(window.location.search);
    const settingsUuid = search.get('settings_uuid') || window.localStorage.getItem('settings_uuid');

    const { authMode, showDemoContent } = useContainer(AdminAuthContainer);
    const { fullScreen, narrowStyle, settingsId, children } = props;
    const [isLoading, setIsLoading] = useState(true);
    const [settingsData, setSettingsData] = useState<any>({ loading: true, settingsData: {} });
    const [openErrorModal, setOpenErrorModal] = useState<boolean>(false);
    const [errorModalMessage, setErrorModalMessage] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [me, setMe] = useState<any>({});
    const [meFetched, setMeFetched] = useState<boolean>(false);
    const { api } = getUrls();

    console.log('settingsProvider settingsId', settingsId);

    const fetchSettingsData = useCallback(async () => {
        try {
            // should get extra data not base data
            const { data } = await axios.get(
                `${api}${GET_USER_DATA}?settings_id=${settingsId}&settings_uuid=${settingsUuid}&auth_mode=${authMode}&demo_content=${showDemoContent}`
            );
            setSettingsData(data);
            setIsLoading(false);
        } catch (err) {
            setErrorMessage(err!.toString());
            setSettingsData(undefined);
            setIsLoading(false);
        }
    }, [api, settingsId, authMode, settingsUuid, showDemoContent]);

    useEffect(() => {
        if (!settingsUuid || !settingsId) {
            return;
        }

        const fetchMe = async () => {
            const getMeResponse = await axios.get(
                processApiPath(GET_USER_DATA, undefined, {
                    settings_uuid: settingsUuid,
                    settings_id: settingsId,
                    auth_mode: authMode
                })
            );
            setMe(getMeResponse);
            setMeFetched(true);
            fetchSettingsData();
        };

        fetchMe();
    }, [api, settingsId, fetchSettingsData, authMode, settingsUuid]);

    return (
        <Provider
            value={{
                settingsId,
                loading: settingsData.loading || isLoading,
                isFullScreen: !!fullScreen,
                isNarrowStyle: !!narrowStyle,
                openErrorModal,
                setOpenErrorModal,
                errorModalMessage,
                setErrorModalMessage,
                settingsData,
                setSettingsData,
                me,
                meFetched,
                errorMessage
            }}
        >
            {children}
        </Provider>
    );
};

export { PortalContext, PortalProvider, Consumer as PortalConsumer };
